import { Box, Stack } from "@mui/material";
import {
  Controller,
  ControllerRenderProps,
  useFormContext,
} from "react-hook-form";

import { MenuItem, Select, Typography } from "@/components/ui-v2";

import {
  COMPRESSION_OPTIONS,
  DEFAULT_FRAME_EXTRACTION_FPS,
  DEFAULT_GIF_FPS,
  ENCODING_UNSET,
  EXPORT_FORM_FIELDS,
  ExportFormState,
  FRAME_EXTRACTION_FPS_OPTIONS,
  GIF_FPS_OPTIONS,
  PNG_SEQUENCE_FORMAT,
  VIDEO_CODEC_OPTIONS,
  VIDEO_FORMAT_OPTIONS,
} from "../../../constants";

export const ExportFormVideoOptions = () => {
  const { watch, setValue, getValues } = useFormContext<ExportFormState>();
  const format = watch(EXPORT_FORM_FIELDS.FORMAT);
  const isWebm = format === "WEBM";
  const isGif = format === "GIF";
  const isPngSequence = format === PNG_SEQUENCE_FORMAT;

  const originalWidth = watch(EXPORT_FORM_FIELDS.ORIGINAL_WIDTH);
  const originalHeight = watch(EXPORT_FORM_FIELDS.ORIGINAL_HEIGHT);

  const handleFormatChange = (value: string, field: ControllerRenderProps) => {
    field.onChange(value);
    if (value === "GIF") {
      setValue(EXPORT_FORM_FIELDS.VIDEO_CODEC, undefined);
      setValue(EXPORT_FORM_FIELDS.COMPRESSION, undefined);
      if (!getValues(EXPORT_FORM_FIELDS.FPS)) {
        setValue(EXPORT_FORM_FIELDS.FPS, DEFAULT_GIF_FPS);
      }
    } else if (value === PNG_SEQUENCE_FORMAT) {
      setValue(EXPORT_FORM_FIELDS.VIDEO_CODEC, undefined);
      setValue(EXPORT_FORM_FIELDS.COMPRESSION, undefined);
      if (!getValues(EXPORT_FORM_FIELDS.FRAME_EXTRACTION_FPS)) {
        setValue(
          EXPORT_FORM_FIELDS.FRAME_EXTRACTION_FPS,
          DEFAULT_FRAME_EXTRACTION_FPS,
        );
      }
    } else if (value === "WEBM") {
      // WEBM is always VP9, so a codec choice has nothing to select.
      setValue(EXPORT_FORM_FIELDS.VIDEO_CODEC, undefined);
    }
    // MP4 and MOV keep whatever the user has chosen, including nothing: an unset codec and
    // compression mean the source's encoding is carried across rather than re-made.
  };

  return (
    <>
      {/* Format */}
      <Box>
        <Controller
          name={EXPORT_FORM_FIELDS.FORMAT}
          render={({ field }) => (
            <Select
              {...field}
              fullWidth
              label="Format"
              onChange={(event) => {
                handleFormatChange(event.target.value as string, field);
              }}
              size="small"
            >
              {VIDEO_FORMAT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </Box>

      {/* GIF Options: Frame rate */}
      {isGif && (
        <Box>
          <Controller
            name={EXPORT_FORM_FIELDS.FPS}
            render={({ field }) => (
              <Select
                {...field}
                value={field.value ?? DEFAULT_GIF_FPS}
                fullWidth
                label="Frame Rate (FPS)"
                size="small"
              >
                {GIF_FPS_OPTIONS.map((fps) => (
                  <MenuItem key={fps} value={fps}>
                    {fps} fps
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </Box>
      )}

      {/* PNG Sequence Options: Frame extraction rate */}
      {isPngSequence && (
        <Box>
          <Controller
            name={EXPORT_FORM_FIELDS.FRAME_EXTRACTION_FPS}
            render={({ field }) => (
              <Select
                {...field}
                value={field.value ?? DEFAULT_FRAME_EXTRACTION_FPS}
                fullWidth
                label="Frame Rate (FPS)"
                size="small"
              >
                {FRAME_EXTRACTION_FPS_OPTIONS.map((fps) => (
                  <MenuItem key={fps} value={fps}>
                    {fps} fps
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </Box>
      )}

      {/* Encoding: Codec + Compression side by side */}
      {!isGif && !isPngSequence && (
        <Stack spacing={6} sx={{ maxWidth: 100 }}>
          <Typography variant="small" fontWeight={600}>
            Encoding
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "row", gap: 3 }}>
            <Box>
              <Controller
                name={EXPORT_FORM_FIELDS.COMPRESSION}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value ?? ENCODING_UNSET}
                    onChange={(event) =>
                      field.onChange(
                        event.target.value === ENCODING_UNSET
                          ? undefined
                          : event.target.value,
                      )
                    }
                    fullWidth
                    label="Compression"
                    size="small"
                  >
                    <MenuItem value={ENCODING_UNSET}>Original</MenuItem>
                    {COMPRESSION_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </Box>
            {!isWebm && (
              <Box>
                <Controller
                  name={EXPORT_FORM_FIELDS.VIDEO_CODEC}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value ?? ENCODING_UNSET}
                      onChange={(event) =>
                        field.onChange(
                          event.target.value === ENCODING_UNSET
                            ? undefined
                            : event.target.value,
                        )
                      }
                      fullWidth
                      label="Codec"
                      size="small"
                    >
                      {/* Not "Original": the source codec only survives while nothing else
                          forces a re-encode. Asking for High compression makes the server
                          pick H.264. */}
                      <MenuItem value={ENCODING_UNSET}>Automatic</MenuItem>
                      {VIDEO_CODEC_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Box>
            )}
          </Box>
        </Stack>
      )}

      {/* Video Size (read-only) */}
      {originalWidth && originalHeight && (
        <Stack spacing={3}>
          <Typography variant="small" fontWeight={600}>
            {isGif || isPngSequence ? "Size" : "Video Size"}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Typography variant="small" color="text.secondary">
              Width
            </Typography>
            <Typography variant="small">{originalWidth} px</Typography>
          </Stack>
          <Stack direction="row" spacing={2}>
            <Typography variant="small" color="text.secondary">
              Height
            </Typography>
            <Typography variant="small">{originalHeight} px</Typography>
          </Stack>
        </Stack>
      )}
    </>
  );
};
